/**
 * ARBETSORDERN — detaljen. Prototypens data-screen="order".
 *
 * "ATT GÖRA" — KRYSSRUTOR UR BESKRIVNINGENS RADER. Prototypen har en bockbar
 * checklista med räkning ("1 / 3"), och det är så skärmen ritas: varje RAD i
 * work_order.description blir en kryssrad. schema/company.sql säger om fältet att
 * det "bär både rubrik och att-göra-text", så radbrytningarna är chefens egen
 * uppdelning och den enda vi har.
 *
 * KRYSSEN LEVER BARA I SKÄRMEN, och det är en LUCKA värd att rapportera: någon
 * tabell med att-göra-rader per arbetsorder finns inte i kontraktet (task är
 * firmans gemensamma MOMENTLISTA, det man väljer i tidraden — inte punkter på ett
 * jobb). Bockarna försvinner därför när skärmen lämnas. Rätt fix är ett fält i
 * schemat, inte en lokal lagring som låtsas vara delad.
 *
 * BESLUT 16 I VARJE VY. Skärmen läser ur `Arbetsorderlager`, som bara bär
 * *WorkerRow-typerna: det finns inget timpris att visa, inte ens för chefen, och
 * inte heller någon summa som går att räkna baklänges. Det enda beloppet är
 * utläggens egna, som användaren själv skrev in ex moms.
 *
 * FOTO ÄR INTE BYGGT ÄN. FILE_UPLOAD och FILE_DOWNLOAD svarar 501
 * (worker/src/modules/steg3/index.ts), så skärmen säger det med <EjByggd> i
 * stället för att visa en knapp som ger ett kryptiskt fel. Antalet foton är
 * däremot sant — fotoraderna kommer i SYNC_PULL.
 * RÖSTEN ÄR BYGGD och bor på tidrapporten (Tidny.tsx): knappen här hoppar dit,
 * för det är i det formuläret utkastet fylls i.
 */

import { useRef, useState } from 'react';
import type { MaterialUnit, OpId, SyncOp, Version, WorkOrderWorkerRow } from '@delat/types';
import {
  Bricka,
  EjByggd,
  Felruta,
  Ikonrad,
  Knapp,
  Kravbricka,
  Kryssrad,
  Laddar,
  Listrad,
  Materialrad,
  Rapportrad,
  Rostknapp,
  Rubrik,
  Tomtext,
  Yta,
} from '../ui';
import { apifel, atgardFor } from '../api';
import type { Klartidfel } from '../api';
import { useInloggad } from '../app/session';
import { useNavigering, usePlats } from '../app/navigering';
import { visaMil, visaMinuter, visaOren, visaTusendel } from '../app/datum';
import {
  adressFor,
  forstaMisslyckade,
  kundMedId,
  medlemsnamn,
  nu,
  nyttOpId,
  orderMedId,
  rapporteratPa,
  skickaOps,
  useArbetsorderlager,
} from './arbetsorder-data';
import type { Arbetsorderlager } from './arbetsorder-data';

/** Enheterna i MaterialUnit som svensk text. 'h' finns med flit inte. */
const ENHETSTEXT: Record<MaterialUnit, string> = {
  pcs: 'st',
  m: 'm',
  m2: 'm²',
  m3: 'm³',
  kg: 'kg',
  l: 'l',
  pkg: 'förp',
};

/**
 * Kartlänken. Adressen är det enda vi har — kontraktet bär inga koordinater —
 * så sökningen får göra resten. Länken öppnas i en ny flik och appen släpper
 * aldrig ifrån sig något annat än just adressraden.
 */
const kartlank = (adress: string): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(adress)}`;

/**
 * ATT-GÖRA-PUNKTERNA UR BESKRIVNINGEN. En rad = en punkt; tomma rader faller
 * bort så att dubbla radbrytningar inte blir tomma kryssrutor. Är hela texten
 * ett enda stycke blir det EN punkt, och det är rätt: då har chefen skrivit den
 * som ett stycke.
 */
const attGoraPunkter = (beskrivning: string): readonly string[] =>
  beskrivning
    .split('\n')
    .map((rad) => rad.trim())
    .filter((rad) => rad !== '');

export const Order = () => {
  const { firmaId, arChef, arLast, vy } = useInloggad();
  const { params } = usePlats();
  const { ga } = useNavigering();
  const { lage, laddaOm } = useArbetsorderlager(firmaId);
  const [sparar, sattSparar] = useState(false);
  const [sparfel, sattSparfel] = useState<Klartidfel | null>(null);
  /**
   * OP_ID MYNTAS EN GÅNG (beslut 09) och återanvänds vid varje omsändning, så
   * att ett omtag spelar upp det gamla svaret i stället för att skriva igen.
   *
   * VERSIONEN LIGGER MED, och den måste göra det: idempotensen slår upp på
   * (op_id, konto, enhet) OCH request_hash, och base_version ingår i hashen.
   * Efter ett SYNC_CONFLICT läser skärmen om, raden står på en NY version, och
   * en omsändning med SAMMA op_id men ny base_version vore samma nyckel med en
   * annan kropp — alltså DUPLICATE_OP. Byter versionen är det en annan
   * skrivning, och då ska den ha ett eget op_id.
   */
  const opRef = useRef<{ readonly opId: OpId; readonly forVersion: Version } | null>(null);
  /** Ikryssade att-göra-punkter. Lever bara i skärmen — se filhuvudet. */
  const [klara, sattKlara] = useState<ReadonlySet<number>>(() => new Set());
  const orderId = params['work_order_id'] ?? '';

  if (lage.status === 'laddar') return <Laddar rader={5} />;
  if (lage.status === 'fel') {
    return (
      <Felruta
        fel={lage.fel}
        atgard={
          <Knapp variant="piller" onClick={laddaOm}>
            Försök igen
          </Knapp>
        }
      />
    );
  }

  const lager: Arbetsorderlager = lage.lager;
  const order = orderMedId(lager, orderId);
  if (order === null) {
    // Samma besked oavsett om ordern är raderad, hör till en annan firma eller
    // — för en gäst — aldrig var hans att se (beslut 17).
    return (
      <>
        <Felruta fel={apifel('UNKNOWN_WORK_ORDER', { id: orderId }, '')} />
        <Knapp variant="stor-tyst" onClick={() => ga('ordrar')}>
          Till arbetsordrarna
        </Knapp>
      </>
    );
  }

  const kund = kundMedId(lager, order.customer_id);
  const ansvarig = medlemsnamn(lager, order.responsible_account_id);
  const adress = adressFor(order, kund);
  const gjort = rapporteratPa(lager, order.id);
  const material = lager.material.filter((rad) => rad.work_order_id === order.id);
  const foton = lager.foton.filter((rad) => rad.work_order_id === order.id);
  const harAdress = order.address !== null || (kund?.address ?? null) !== null;
  const punkter = attGoraPunkter(order.description);

  /**
   * MARKERA KLAR / ÖPPNA IGEN. Bara chefen: 'put:work_order' är MANAGER_ONLY i
   * SYNC_RULES, och bokföraren förekommer inte i en enda rad där (beslut 07).
   *
   * HELA RADEN SKRIVS TILLBAKA, ur den FULLA ordern. WorkOrderWrite kräver
   * price_list_id, och att skicka null för att den smala raden inte bar den hade
   * tyst raderat arbetsorderns prisundantag — och därmed ändrat priset på allt
   * som rapporteras efteråt (beslut 15).
   */
  const vaxlaStatus = (aktuell: WorkOrderWorkerRow): void => {
    const full = lager.fullOrder(aktuell.id);
    if (full === null) return;
    const tidigare = opRef.current;
    const opId =
      tidigare !== null && tidigare.forVersion === full.version ? tidigare.opId : nyttOpId();
    opRef.current = { opId, forVersion: full.version };
    const op: SyncOp = {
      op_id: opId,
      client_time: nu(),
      kind: 'put',
      base_version: full.version,
      table: 'work_order',
      id: full.id,
      row: {
        customer_id: full.customer_id,
        address: full.address,
        description: full.description,
        status: full.status === 'open' ? 'done' : 'open',
        responsible_account_id: full.responsible_account_id,
        price_list_id: full.price_list_id,
      },
    };
    sattSparar(true);
    sattSparfel(null);
    void (async () => {
      const svar = await skickaOps(firmaId, [op]);
      sattSparar(false);
      if (!svar.ok) {
        sattSparfel(svar.fel);
        return;
      }
      // Ett giltigt kuvert svarar ALLTID 200 — utfallet står per operation.
      const fel = forstaMisslyckade(svar.varde);
      if (fel !== null) {
        sattSparfel(fel);
        return;
      }
      opRef.current = null;
      // Klienten kör alltid en pull direkt efter en lyckad push.
      laddaOm();
    })();
  };

  return (
    <>
      <Rubrik
        titel={adress}
        under={
          <>
            {kund === null ? 'Okänd kund' : kund.name}
            {kund !== null && ` · ${kund.kind === 'private' ? 'Privatkund' : 'Företagskund'}`}
            {' · '}
            {ansvarig === null ? 'Ingen ansvarig' : `Ansvarig: ${ansvarig}`}
            {order.status === 'done' && (
              <>
                {' '}
                <Bricka ton="klar">Klar</Bricka>
              </>
            )}
            {kund?.kind === 'private' && (
              <>
                {' '}
                <Kravbricka>Privatkund — moment krävs</Kravbricka>
              </>
            )}
          </>
        }
      />

      <Knapp
        ikon="plats"
        disabled={!harAdress}
        title={harAdress ? undefined : 'Ordern har ingen adress och kunden ingen heller.'}
        onClick={() => {
          globalThis.open(kartlank(adress), '_blank', 'noopener,noreferrer');
        }}
      >
        Hitta hit
      </Knapp>

      <Yta
        rubrik="Att göra"
        ikon="bock"
        rakning={punkter.length === 0 ? undefined : `${klara.size} / ${punkter.length}`}
      >
        {punkter.map((rad, i) => (
          <Kryssrad
            key={`${i}-${rad}`}
            form="todo"
            ikryssad={klara.has(i)}
            onToggle={() =>
              sattKlara((forra) => {
                const nasta = new Set(forra);
                if (!nasta.delete(i)) nasta.add(i);
                return nasta;
              })
            }
          >
            {rad}
          </Kryssrad>
        ))}
        {punkter.length === 0 && <Tomtext>Chefen har inte skrivit något än.</Tomtext>}
      </Yta>

      <Yta rubrik="Rapportera" rakning="på det här jobbet">
        <Rapportrad>
          <Knapp variant="rapport" ikon="tid" onClick={() => ga('tidny')}>
            Tid
          </Knapp>
          <Knapp
            variant="rapport"
            disabled
            title="Material rapporteras inte i appen än."
          >
            Material
          </Knapp>
          <Knapp variant="rapport" ikon="mil" onClick={() => ga('mil')}>
            Mil
          </Knapp>
          <Knapp variant="rapport" ikon="utlagg" onClick={() => ga('utlagg')}>
            Utlägg
          </Knapp>
        </Rapportrad>
        <Rostknapp online={globalThis.navigator.onLine} onClick={() => ga('tidny')}>
          Säg vad du gjort i stället
        </Rostknapp>
        <Ikonrad ikon="ordrar">
          Säg adressen — <em>{adress}</em> — så hittar rösten jobbet.
        </Ikonrad>
      </Yta>

      <Yta rubrik="Rapporterat" rakning={visaMinuter(gjort.minuter)}>
        <Listrad titel="Tid" varde={visaMinuter(gjort.minuter)} />
        <Listrad titel="Material" varde={`${material.length} rader`} />
        <Listrad titel="Mil" varde={visaMil(gjort.meter)} />
        <Listrad titel="Utlägg" bi="ex moms" varde={visaOren(gjort.utlaggOren)} />
        <Tomtext>
          {vy === 'full' ? 'Allt, av alla.' : 'Dina egna. Materialet är hela jobbets.'}
        </Tomtext>
      </Yta>

      <Yta rubrik="Material" rakning={`${material.length} rader`}>
        {material.map((rad) => (
          <Materialrad
            key={rad.id}
            titel={rad.description}
            varde={`${visaTusendel(rad.quantity_milli)} ${ENHETSTEXT[rad.unit]}`}
          />
        ))}
        {material.length === 0 && <Tomtext>Inget material bokfört än.</Tomtext>}
      </Yta>

      <Yta rubrik="Foto" ikon="kamera" rakning={`${foton.length} st`}>
        <EjByggd vad="Foto på arbetsordern" />
      </Yta>

      {sparfel !== null && (
        // 'give_up' i shared/errors.ts betyder att en omsändning ALDRIG kan
        // lyckas — SYNC_CONFLICT, ROW_INVOICED, VALIDATION_FAILED. Då är det
        // läget som ändrats, och rätt knapp är att hämta om det, inte att skicka
        // samma skrivning en gång till.
        <Felruta
          fel={sparfel}
          atgard={
            atgardFor(sparfel) === 'give_up' ? (
              <Knapp
                variant="piller"
                disabled={sparar}
                onClick={() => {
                  sattSparfel(null);
                  laddaOm();
                }}
              >
                Ladda om
              </Knapp>
            ) : (
              <Knapp variant="piller" disabled={sparar} onClick={() => vaxlaStatus(order)}>
                Försök igen
              </Knapp>
            )
          }
        />
      )}

      {arChef && (
        <div style={{ marginTop: 'var(--gap-kort)' }}>
          <Knapp
            variant="stor-tyst"
            disabled={arLast || sparar}
            title={arLast ? 'Prenumerationen är pausad. Inget nytt kan sparas.' : undefined}
            onClick={() => vaxlaStatus(order)}
          >
            {sparar ? 'Sparar...' : order.status === 'open' ? 'Markera klar' : 'Öppna igen'}
          </Knapp>
        </div>
      )}

      <Tomtext>Ett klart jobb går fortfarande att rapportera på.</Tomtext>
    </>
  );
};
